import { useCallback, useEffect, useRef } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringSuggestProvider } from 'libs/searchspring/fetchers/suggest'
import { SearchspringTrendingProvider } from 'libs/searchspring/fetchers/trending'
import { SearchPageLink } from 'libs/searchspring/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/searchspring/components/quick-search/QuickSearchResults'
import { QuickSearchBlogSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchBlogResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { getRecentSearches } from '@/components/helpers/recent-searches'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { withRecommendationProvider } from 'libs/searchspring/components/recommendations/withRecommendationsProvider'
import { CarouselResults } from 'libs/searchspring/components/recommendations/CarouselResults'
import { calculateProductCount } from 'libs/searchspring/components/collection/filters/ProductCount'

const RECENT_SEARCHES_DROPDOWN_MAX = 4

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter((s) => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter((s) => s.type === 'pages') || []
const popularProductsProfile = globalState.siteIds?.popularProductsProfile || 'api-test'

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * ViewResultsButton component - Renders the "View Results" button for search results
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {number} props.totalResults - Total number of search results
 * @param {boolean} props.isMobile - Whether to show mobile-specific styling
 * @returns {JSX.Element} Rendered component
 */
export const ViewResultsButton = ({ query, totalResults }) => {
  if (!query || !totalResults) return null

  return (
    <SearchPageLink query={query} className="btn btn-m lg:btn-l btn-fill btn-primary w-full">
      View {totalResults} Results
    </SearchPageLink>
  )
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect, onResultsUpdate }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useSearchspringContext()
  const { title, hasResults, showNoResults, isCheckingResults, isRecommendationsReady, isResultsReady } =
    useSearchResultState({ query, results, isLoading })
  const Recommendations = useCallback(withRecommendationProvider(CarouselResults, 'recommend'), [])

  const itemClass = 'w-full'
  const carouselItemClass = 'w-grid-7/8 lg:w-full max-w-[280px] lg:max-w-grid-1/4 mr-gutter [_&>div]:w-full'
  const productCardSettings = {
    useMiniCard: false,
    hideQuickAdd: true,
    hideSwatches: false,
    hidePromolines: false,
    hideBadges: false,
    hideWishlist: false
  }
  const carouselSettings = {
    draggable: true,
    freeScroll: false,
    wrapAround: false,
    groupCells: true,
    fade: false,
    adaptiveHeight: false,
    accessibility: true,
    cellAlign: 'left',
    prevNextButtons: false,
    pageDots: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  useEffect(() => {
    if (onResultsUpdate && results) {
      const totalResults = calculateProductCount(results)
      const hasResults = totalResults > 0
      onResultsUpdate({ hasResults, totalResults })
    }
  }, [results, onResultsUpdate])

  /**
   * Renders suggestion components based on current search state.
   * In NO_SEARCH state: if recent searches exist, show "Recent Searches" (up to 4) + Popular Pages; otherwise Popular Searches + Popular Pages.
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    let config
    if (query && hasResults) {
      config = SEARCH_CONFIG.HAS_RESULTS
    } else if (query && !hasResults) {
      config = SEARCH_CONFIG.NO_RESULTS
    } else {
      const recentList = getRecentSearches().slice(0, RECENT_SEARCHES_DROPDOWN_MAX)
      if (recentList.length > 0) {
        config = {
          ...SEARCH_CONFIG.NO_SEARCH,
          suggestions: [
            {
              title: 'Recent Searches',
              type: 'recent',
              default: recentList.map((q) => ({ query: q, label: q })),
              isStatic: true
            },
            { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
          ]
        }
      } else {
        config = SEARCH_CONFIG.NO_SEARCH
      }
    }
    const suggestionQuery = query && !hasResults ? '' : query
    return config.suggestions.map((suggestion, index) => {
      if (suggestion.type === 'pages' && query) {
        return (
          <div key={index} className="relative flex flex-col gap-y-1.25 border-r border-neutral-medium px-margin py-1.5 last:!border-b-0 lg:border-b lg:border-r-0 lg:py-2.5">
            <QuickSearchBlogSuggestions
              query={query}
              fallbackTitle={suggestion.title}
              fallbackSuggestions={suggestion.default}
              fallbackQuery={suggestionQuery}
            />
          </div>
        )
      }
      return (
        <SearchSuggestions
          key={index}
          title={suggestion.title}
          type={suggestion.type}
          query={suggestionQuery}
          defaultSuggestions={suggestion.default}
          isStatic={suggestion.isStatic}
        />
      )
    })
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or PopularCategories component
   */
  return (
    <div className="grid max-h-[80vh] auto-rows-auto overflow-y-scroll overscroll-none bg-secondary no-scrollbar">
      <div className="grid shrink-0 lg:grid-cols-main lg:gap-y-1.5">
        {/* Main Content Section */}
        <div className="col-[1/2] grid h-fit w-full auto-rows-auto gap-y-1.875 overflow-x-hidden px-margin py-1.5 lg:col-[4/-1] lg:row-[1/2] lg:gap-y-2.5 lg:py-2.5">
          <div className="flex w-full flex-col gap-y-1.25">
            <h2 className="h5">{title}</h2>

            <div className="w-full">
              {/* Loading Overlay */}
              <div
                className={clsx(
                  'overflow-hidden w-full',
                  isCheckingResults || isLoading ? '' : 'pointer-events-none h-0 opacity-0'
                )}
              >
                <SkeletonGrid carouselSettings={carouselSettings} itemClass={itemClass} />
              </div>
              <div className={clsx('w-full', hasResults ? '' : 'hidden')}>
                {isResultsReady && <QuickSearchResults />}
              </div>
              <div
                className={clsx('w-full', !hasResults && query && !isLoading && showNoResults ? '' : 'hidden')}
                data-dl-section-id="api-test"
              >
                {isRecommendationsReady && (
                  <Recommendations
                    profile={popularProductsProfile}
                    limit={4}
                    itemClass={carouselItemClass}
                    productCardSettings={productCardSettings}
                    carouselSettings={carouselSettings}
                  />
                )}
              </div>
              <div className={clsx('w-full', query ? 'hidden' : '')}>
                {popularCategories.current && (
                  <PopularCategories content={popularCategories.current} carouselSettings={carouselSettings} />
                )}
              </div>
            </div>
          </div>

          {/* Mobile "View results" button */}
          {hasResults && !redirect && (
            <div className="lg-max:row-[1]">
              <ViewResultsButton query={query} totalResults={calculateProductCount(results)} />
            </div>
          )}
        </div>

        {/* Suggestions Section */}
        <div
          className={clsx(
            'col-[1/2] grid h-fit w-full grid-cols-2 border-neutral-medium lg:col-[1/4] lg:row-[1/2] lg:flex lg:h-full lg:flex-col lg:border-r',
            query && !isLoading ? 'lg-max:border-t' : 'row-[1] lg-max:border-b'
          )}
        >
          {renderSuggestions()}
        </div>
      </div>

      {/* No Results Section */}
      {!hasResults && query && !isLoading && showNoResults && (
        <div class="row-[1] flex w-full flex-col border-b border-neutral-medium bg-system-error-light px-margin py-1">
          <span className="paragraph-s">No results for "{query}". Please try another search term.</span>
        </div>
      )}
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content, carouselSettings }) => {
  return (
    <x-carousel
      className="flex h-fit w-full"
      carousel-settings={JSON.stringify(carouselSettings)}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {Object} props.carouselSettings - Settings for the carousel
 * @param {string} props.itemClass - CSS class for carousel items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ carouselSettings, itemClass }) => {
  return (
    <div
      className="grid h-fit w-full grid-cols-2 gap-gutter md:grid-cols-3 lg:grid-cols-4 md-max:w-full-margin md-max:-ml-margin"
      carousel-settings={JSON.stringify(carouselSettings)}
    >
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={clsx(itemClass, 'md:!hidden lg:!flex')} />
    </div>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections', 'pages', or 'recent')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, isStatic = false }) => {
  return (
    <div className="relative flex flex-col gap-y-1.25 border-r border-neutral-medium px-margin py-1.5 last:!border-b-0 lg:border-b lg:border-r-0 lg:py-2.5">
      <h3 className="h5">{title}</h3>
      {type === 'recent' ? (
        <div className="flex w-full flex-grow flex-col gap-y-1.25">
          {(defaultSuggestions || []).map((suggestion, index) => (
            <SearchPageLink key={`recent-${index}`} query={suggestion.query} className="group w-fit">
              <span className="paragraph-b w-fit first-letter:capitalize">{suggestion.label}</span>
            </SearchPageLink>
          ))}
        </div>
      ) : isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : query ? (
        <SearchspringSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </SearchspringSuggestProvider>
      ) : (
        <SearchspringTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </SearchspringTrendingProvider>
      )}
    </div>
  )
}
